import type { NetworkAdapter } from '../adapters/network';
import { getPersonId } from '../analytics/person-id';
import type { BrowserRevealData } from '../browserReveal';
import { FRONTEND_VERSION, isVersionCompatible, MAX_QUERY_CHARS } from '../config/constants';
import {
    getDeploymentTarget,
    isWidgetTestModeStored,
    setWidgetTestMode,
    updateDeploymentTarget,
} from '../config/deployment';
import { getClientPostConversionActivation } from '../config/PostConversionFormSubmitTracker';
import { flushSnitcherData, getSnitcherSessionId } from '../hooks/useSnitcherRadar';
import { getUserLocale, normalizeConversationLanguage } from '../utils/content/languageUtils';
import { logger } from '../utils/core/logger';
import { classifyStreamingError } from '../utils/core/streamErrorClassifier';
import { readPageAlternates } from '../utils/domain/pageAlternates';
import { conversationScope } from './conversationScope';
import { getIdentificationConsent, isIdentificationAllowed } from './identificationConsent';

// Some iOS Safari contexts (Lockdown Mode, sandboxed iframes lacking
// `allow-same-origin`, storage explicitly disabled) expose `sessionStorage`
// as `null` or throw on access. Widget conversation continuity, backend
// session linking, and analytics correlation all hinge on a stable session
// ID across the visit — degrading to in-memory would silently break those
// invariants. Surface a clean sentinel error so callers can skip widget
// initialization instead of crashing later on `null.getItem(...)`.
export class SessionStorageUnavailableError extends Error {
    constructor(public readonly reason: string) {
        super(`sessionStorage unavailable: ${reason}`);
        this.name = 'SessionStorageUnavailableError';
    }
}

// Returns the validated Storage so callers keep using it: on some WebKit hosts the
// bare `sessionStorage` global is null while `window.sessionStorage` works (ROSE-FRONT-1Y3).
const assertSessionStorageAvailable = (): Storage => {
    try {
        if (typeof window === 'undefined') {
            throw new SessionStorageUnavailableError('window is undefined');
        }
        const storage = window.sessionStorage;
        if (!storage) {
            throw new SessionStorageUnavailableError('property is null or undefined');
        }
        const probeKey = '__inboundx_storage_probe__';
        storage.setItem(probeKey, '1');
        storage.removeItem(probeKey);
        return storage;
    } catch (err) {
        if (err instanceof SessionStorageUnavailableError) {
            throw err;
        }
        throw new SessionStorageUnavailableError(
            err instanceof Error ? err.message : 'access threw'
        );
    }
};

interface InboundXConfig {
    apiUrl: string;
    siteName: string;
    /** Fresh server-loaded workspace config requires an uncached routing lookup. */
    workspaceRouting?: boolean;
    /** Page sent instead of `window.location.href` by a hosting surface (PreviewHost.pageUrl). */
    pageUrl?: string;
    apiKey?: string;
    model?: string;
    networkAdapter?: NetworkAdapter;
    conversationLanguage?: string;
    visitorIp?: string;
    browserRevealData?: BrowserRevealData;
    /**
     * Supplies the signed-in backoffice user's access token for preview surfaces. Only the
     * chat stream sends it, and only when a host provides one — a widget on a client site
     * has no host, so its requests carry no identity beyond the shared widget API key.
     */
    getPreviewAuthToken?: () => Promise<string | null>;
}

interface InboundXResponse {
    content: string;
    error?: string;
    sessionId?: string;
    messageId?: string;
    visitor_profile?: VisitorProfile | null;
}

export interface VisitorProfile {
    company_name?: string;
    company_description?: string | null;
    domain?: string | null;
    sector?: string | null;
    sub_sector?: string | null;
    sector_confidence_level?: string | null;
    email?: string | null;
    feature_list?: string[];
    intent?: string;
    job_to_be_done?: string | null;
    enrichment_status?: string;
    enrichment_source?: string | null;
    enrichment_ip_address?: string | null;
    enriched_at?: string;
    enrichment_error_message?: string | null;
}

/**
 * Booking context sent when CTA click triggers email collection flow.
 * Used by backend to route to HANDLE_BOOKING action.
 */
export interface BookingContext {
    /** Whether this message is part of the booking flow */
    is_booking_flow: boolean;
    /** The CTA ID that triggered the booking flow */
    cta_id: string;
    /** The target URL to redirect to after email collection */
    redirect_url: string;
}

export type PostRequestCaptureSource =
    | 'fetch'
    | 'xhr'
    | 'send_beacon'
    | 'native_form_submit'
    | 'native_form_direct_submit';

export type PostRequestCapturePayloadKind =
    | 'empty'
    | 'json'
    | 'form_urlencoded'
    | 'form_data'
    | 'text'
    | 'unreadable';

interface PostRequestCaptureFileMetadata {
    fieldName?: string;
    name: string;
    size: number;
    type?: string;
    lastModified?: number;
}

// FIXME: The "rw_client_post_request" naming convention (used in backend/analytics) is misleading —
// it follows the "rw_client_*" pattern which suggests a PostHog event, but this data is sent to
// our backend API (/api/post-capture), not to PostHog. Consider renaming to avoid confusion
// (e.g. "captured_post_request" or similar).
interface QueryRequestBody {
    query: string;
    siteName: string;
    workspaceRouting?: boolean;
    sessionId: string;
    messageId: string;
    messageSource?: string;
    bookingContext?: BookingContext;
    personId?: string;
    deploymentTarget?: string;
    model?: string;
    conversationLanguage?: string;
    visitorIp?: string;
    browserRevealData?: BrowserRevealData;
    snitcherSessionId?: string;
    identificationConsent?: boolean;
    pageUrl?: string;
    pageAlternates?: Record<string, string>;
    isFormAssistant?: boolean;
    isPostConversion?: boolean;
    postConversionContext?: Record<string, string>;
    postConversionFormId?: string;
    // Staff/playground-only per-request override of the client's behavior.playbook.
    // Set from the playground "Playbook" force toggle; omitted (undefined) for normal
    // widget traffic so the server resolves from config.
    playbook?: 'information' | 'conversion';
    // Staff/playground-only per-request override of the client's behavior.demo_timing (IX-4063).
    // Set from the playground "Demo timing" force toggle; omitted for normal widget traffic.
    demoTiming?: 'interest_score' | 'readiness_judge';
    // Staff/playground-only per-request override of the client's behavior.response_tone.
    // Set from the playground "Tone" control; omitted for normal widget traffic. Ignored
    // by the server in production.
    responseTone?: string;
}

export interface PostRequestCaptureEvent {
    siteName: string;
    sessionId: string;
    personId?: string;
    pageUrl: string;
    capturedAt: string;
    source: PostRequestCaptureSource;
    requestUrl: string;
    requestMethod: 'POST';
    contentType?: string;
    payloadKind: PostRequestCapturePayloadKind;
    payloadJson?: unknown;
    payloadText?: string;
    hasFiles: boolean;
    filesMeta: PostRequestCaptureFileMetadata[];
    formAction?: string;
    formMethod?: string;
    formId?: string;
    formClass?: string;
}

/**
 * Which knowledge the retriever pulled for one answer. Sent by the chat API only to
 * preview surfaces (backoffice Playground), and forwarded to the host page through the
 * `inboundx_knowledge_sources` window event — the widget itself never renders it.
 */
export interface KnowledgeSourcesMetadata {
    /** Documents read as text, deduplicated per source path, in retrieval order. */
    sources: { file_path: string; chunks: number }[];
    /**
     * Graph entities and relationships retrieved for this answer. The retrieved unit is the
     * extracted fact itself, so it carries its own text plus the documents it came from.
     */
    graph_facts: {
        kind: 'entity' | 'relationship';
        label: string;
        content: string;
        file_paths: string[];
    }[];
    rewritten_query?: string | null;
    /** The workspace that answered; `null` when the turn fell back to hostname routing. */
    workspace_id?: string | null;
}

/** Window event carrying {@link KnowledgeSourcesMetadata} for the answer that just completed. */
export const KNOWLEDGE_SOURCES_EVENT = 'inboundx_knowledge_sources';

export type KnowledgeSourcesEventDetail = KnowledgeSourcesMetadata & { messageId: string };

export interface StreamingResponse {
    type: 'thinking' | 'token' | 'complete' | 'error' | 'stream_error';
    content: string;
    site?: string;
    sessionId?: string;
    metadata?: {
        visitor_profile?: VisitorProfile | null;
        suggested_follow_ups?: string[]; // Follow-up questions to continue conversation
        suggested_answers?: string[]; // Pre-suggested answers when bot asks a question
        booking_state?: {
            is_booking_flow: boolean;
            redirect_url?: string | null;
            pending_booking_fields?: string[];
            collected_booking_fields?: Record<string, string>;
        } | null;
        profiling_progress?: {
            current_question: number;
            total_fields: number;
            answered_fields: number;
            is_complete: boolean;
        } | null;
        cta_url_overrides?: Record<string, string> | null;
        /** Classified visitor intent of this turn (e.g. `support`); gates intent-specific CTAs. */
        intent?: string | null;
        /** Playground-only retrieval provenance; absent for production widget traffic. */
        knowledge_sources?: KnowledgeSourcesMetadata | null;
        [key: string]: unknown;
    };
    // For stream_error type
    error?: string;
    message?: string;
    debug_info?: {
        error_type: string;
        error_message: string;
        stack_trace: string;
        site_name: string;
        session_id: string;
        timestamp: string;
    };
}

/**
 * Read the API's public /health payload (version and IX_ENVIRONMENT).
 *
 * Sends only CORS-simple headers so the request does not trigger a preflight
 * OPTIONS: some visitor networks/extensions break the preflight, which would
 * silently fail service initialization and suppress widget_impression (IX-2921).
 */
export function fetchApiHealth(apiUrl: string): Promise<ApiHealth> {
    // IX-5784: the bootstrap support check and ConfigProvider share one request per
    // page; a failure is evicted so their retries fetch again.
    let request = apiHealthRequests.get(apiUrl);
    if (!request) {
        request = requestApiHealth(apiUrl);
        apiHealthRequests.set(apiUrl, request);
        request.catch(() => apiHealthRequests.delete(apiUrl));
    }
    return request;
}

type ApiHealth = { version?: string; environment?: string; status: string };

const apiHealthRequests = new Map<string, Promise<ApiHealth>>();

async function requestApiHealth(apiUrl: string): Promise<ApiHealth> {
    // /health is public (backend exempts it from API-key middleware).
    const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
        // "HTTP error! status: N" is what isRetryableError reads; HTTP/2 has no statusText.
        throw new Error(
            `Health check failed: HTTP error! status: ${response.status} ${response.statusText}`.trim()
        );
    }
    const data = await response.json();
    return { version: data.version, environment: data.environment, status: 'healthy' };
}

export class InboundXService {
    private config: InboundXConfig;
    private sessionId: string;
    private storage: Storage;
    // Fixed at construction: a service built for one workspace keeps writing that
    // workspace's session even after routing moves its host to another.
    private readonly storageScope: string;

    constructor(config: InboundXConfig) {
        this.config = config;
        this.storageScope = conversationScope(config.siteName);
        // Fail fast if sessionStorage is unavailable. Caller (ConfigProvider)
        // catches and skips widget render; better than rendering a widget
        // whose chat history resets on every interaction.
        this.storage = assertSessionStorageAvailable();
        this.sessionId = this.getOrCreateSessionId();

        logger.debug('🏗️ InboundXService created with config:', {
            apiUrl: config.apiUrl,
            siteName: config.siteName,
            conversationLanguage: config.conversationLanguage,
            hasApiKey: !!config.apiKey,
        });
    }

    /**
     * Fetch API version without needing a full service instance
     * Useful for early initialization checks (e.g., analytics setup)
     */
    static async fetchVersion(apiUrl: string): Promise<{
        version: string;
        environment: string;
    }> {
        const response = await fetch(`${apiUrl}/api/version`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return {
            version: data.version,
            environment: data.environment,
        };
    }

    private getStorageKey(): string {
        const apiUrlHash = this.config.apiUrl.replace(/[^a-zA-Z0-9]/g, '_');
        return `inboundx_session_${this.storageScope}_${apiUrlHash}`;
    }

    private getLanguageStorageKey(): string {
        // Pin the language to the specific session id so a brand-new session
        // re-detects the language from the then-current page.
        return `inboundx_lang_${this.sessionId}`;
    }

    /**
     * Resolve the conversation language ONCE per session and pin it.
     *
     * The language is detected from the page URL / site config at the first
     * message of a conversation, normalized to a backend-supported conversation
     * language, and persisted in sessionStorage (keyed to the session id).
     * Every subsequent message reuses that pinned value — even if the visitor
     * navigates to a page in a different locale mid-conversation (e.g. clicks a
     * content recommendation pointing to a /fr page while chatting in pt).
     * Re-detecting per message made the conversation language flip on
     * navigation (IX-3322).
     *
     * When 'auto' is configured we intentionally do NOT pin: the backend
     * auto-detects the visitor's language from each message.
     */
    private resolveConversationLanguage(): string {
        if (this.config.conversationLanguage === 'auto') {
            return 'auto';
        }

        const storageKey = this.getLanguageStorageKey();
        const pinned = this.storage.getItem(storageKey);
        if (pinned) {
            return pinned;
        }

        const detectedLocale = getUserLocale(
            this.config.conversationLanguage,
            undefined,
            this.config.siteName
        );
        const detected = normalizeConversationLanguage(detectedLocale) ?? 'en';
        this.storage.setItem(storageKey, detected);
        return detected;
    }

    private getOrCreateSessionId(): string {
        // Use sessionStorage to persist session ID during page refreshes
        // but create new session when site is visited again (new browser session)
        // Include API URL in storage key so different endpoints get different sessions
        const storageKey = this.getStorageKey();

        let sessionId = this.storage.getItem(storageKey);

        if (!sessionId) {
            sessionId = this.generateSessionId();
            this.storage.setItem(storageKey, sessionId);
            logger.debug('InboundX Service: Created new session for site visit:', sessionId);
        } else {
            logger.debug('InboundX Service: Using existing session from page refresh:', sessionId);
        }

        return sessionId;
    }

    public clearSessionStorage(): void {
        // Clear session storage for this site (used when new navigation is detected)
        const storageKey = this.getStorageKey();
        // Drop the pinned language too (keyed to the current session id, so this
        // must run before the session id is regenerated).
        this.storage.removeItem(this.getLanguageStorageKey());
        this.storage.removeItem(storageKey);
        logger.debug('InboundX Service: Cleared session storage for', this.config.siteName);
    }

    public forceNewSession(): void {
        // Force create a new session (for new navigation)
        this.clearSessionStorage();
        this.sessionId = this.generateSessionId();

        // Update sessionStorage with the new session ID
        const storageKey = this.getStorageKey();
        this.storage.setItem(storageKey, this.sessionId);

        logger.debug('InboundX Service: Forced new session:', this.sessionId);
    }

    private generateSessionId(): string {
        // Generate a unique session ID for this conversation
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 8);
        return `session_${this.config.siteName}_${timestamp}_${randomStr}`;
    }

    private generateMessageId(): string {
        // Generate a unique message ID for each query
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 8);
        return `msg_${timestamp}_${randomStr}`;
    }

    private createHeaders(): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (this.config.apiKey) {
            headers['X-API-Key'] = this.config.apiKey;
        }
        return headers;
    }

    private createRequestBody(
        query: string,
        messageId: string,
        conversationLanguage?: string,
        messageSource?: string,
        bookingContext?: BookingContext,
        isFormAssistant?: boolean,
        isPostConversion?: boolean
    ): QueryRequestBody {
        // A chat message starting with // marks this browser as test traffic
        // (same convention as the backend "comment, don't store" check). We
        // persist the flag and retag the session as 'widget-test' so the
        // client's own testing — this and every later visit in this browser —
        // drops out of production stats. Clear with ?rose_test=0.
        if (query.trim().startsWith('//') && !isWidgetTestModeStored()) {
            setWidgetTestMode(true);
            updateDeploymentTarget('widget-test');
        }

        const requestBody: QueryRequestBody = {
            query: query.trim(),
            siteName: this.config.siteName,
            sessionId: this.sessionId,
            messageId: messageId,
        };

        // Add message source if provided (for analytics tracking)
        if (messageSource) {
            requestBody.messageSource = messageSource;
        }

        // Add booking context if provided (for email collection flow)
        if (bookingContext) {
            requestBody.bookingContext = bookingContext;
            logger.debug('📧 [Booking Flow] Including booking context in request', {
                ctaId: bookingContext.cta_id,
                isBookingFlow: bookingContext.is_booking_flow,
            });
        }

        // Add PostHog person ID if available (for backend analytics tracking)
        const personId = getPersonId();
        if (personId) {
            requestBody.personId = personId;
        }

        // Add deployment target if available (for backend analytics tracking)
        const deploymentTarget = getDeploymentTarget();
        if (deploymentTarget) {
            requestBody.deploymentTarget = deploymentTarget;
        }

        // Add model parameter if configured
        if (this.config.model) {
            requestBody.model = this.config.model;
        }

        // Add conversation language if provided
        if (conversationLanguage) {
            requestBody.conversationLanguage = conversationLanguage;
        }

        // Add visitor IP if provided (for enrichment)
        if (this.config.visitorIp) {
            requestBody.visitorIp = this.config.visitorIp;
        }

        // Add browser Reveal data if available (for enrichment)
        if (this.config.browserRevealData) {
            requestBody.browserRevealData = this.config.browserRevealData;
            logger.debug('📤 [Browser Reveal] Including browser Reveal data in request', {
                hasCompany: !!this.config.browserRevealData.company,
                type: this.config.browserRevealData.type,
                confidenceScore: this.config.browserRevealData.confidenceScore,
            });
        }

        // Add Snitcher Radar session ID if available (for enrichment)
        const snitcherSessionId = isIdentificationAllowed() ? getSnitcherSessionId() : null;
        if (snitcherSessionId) {
            requestBody.snitcherSessionId = snitcherSessionId;
            logger.debug('📤 [Snitcher Radar] Including session ID in request', {
                snitcherSessionId,
            });
        }

        // Host-relayed identification consent; the backend enforces it on consent-gated sites
        const identificationConsent = getIdentificationConsent();
        if (identificationConsent !== null) {
            requestBody.identificationConsent = identificationConsent;
        }

        // Add current page URL for context (used by redirect handler)
        if (this.config.workspaceRouting) {
            requestBody.workspaceRouting = true;
        }
        if (this.config.pageUrl) {
            requestBody.pageUrl = this.config.pageUrl;
        } else if (typeof window !== 'undefined' && window.location?.href) {
            requestBody.pageUrl = window.location.href;
            // Only here: under a pageUrl override, this document is not that page.
            requestBody.pageAlternates = readPageAlternates();
        }

        // Add form assistant flag for backend routing and skill control
        if (isFormAssistant !== undefined) {
            requestBody.isFormAssistant = isFormAssistant;
        }

        // Add post-conversion flag for qualification flow routing
        if (isPostConversion !== undefined) {
            requestBody.isPostConversion = isPostConversion;
        }
        const clientActivation = isPostConversion
            ? getClientPostConversionActivation(this.config.siteName)
            : null;
        if (clientActivation?.formId) {
            requestBody.postConversionFormId = clientActivation.formId;
        }
        if (clientActivation?.context && Object.keys(clientActivation.context).length > 0) {
            requestBody.postConversionContext = clientActivation.context;
        }
        // Extract URL params for post-conversion context when no explicit client API context exists.
        if (
            isPostConversion &&
            !requestBody.postConversionContext &&
            typeof window !== 'undefined'
        ) {
            const params = new URLSearchParams(window.location.search);
            const context: Record<string, string> = {};
            params.forEach((value, key) => {
                context[key] = value;
            });
            if (Object.keys(context).length > 0) {
                requestBody.postConversionContext = context;
            }
        }

        // Playground-only: force behavior.playbook for testing. Set by the playground
        // Features tab; never present in normal widget traffic, so this is undefined and the
        // server resolves from config for real visitors. Legacy self_serve/sales_led values
        // (older bookmarks) are coerced to the new names (IX-4119).
        if (typeof window !== 'undefined') {
            try {
                const forced = window.localStorage.getItem('rose_preprod_playbook_override');
                if (forced === 'information' || forced === 'self_serve') {
                    requestBody.playbook = 'information';
                } else if (forced === 'conversion' || forced === 'sales_led') {
                    requestBody.playbook = 'conversion';
                }
            } catch (error) {
                logger.debug('playbook override read skipped (localStorage unavailable)', error);
            }

            // Playground-only: force behavior.demo_timing for testing (IX-4063). Undefined for
            // normal widget traffic; the server ignores it in production.
            try {
                const dt = window.localStorage.getItem('rose_preprod_demo_timing_override');
                if (dt === 'interest_score' || dt === 'readiness_judge') {
                    requestBody.demoTiming = dt;
                }
            } catch (error) {
                logger.debug('demo_timing override read skipped (localStorage unavailable)', error);
            }

            // Playground-only: force behavior.response_tone for testing. The server honours
            // this only for backoffice-playground traffic (and non-prod tooling); production
            // widget traffic is ignored so real visitors cannot inject tone instructions.
            try {
                const tone = window.localStorage.getItem('rose_preprod_response_tone_override');
                if (tone) {
                    requestBody.responseTone = tone;
                }
            } catch (error) {
                logger.debug(
                    'response_tone override read skipped (localStorage unavailable)',
                    error
                );
            }
        }

        logger.debug('📤 Request body created:', {
            siteName: requestBody.siteName,
            sessionId: requestBody.sessionId,
            conversationLanguage: requestBody.conversationLanguage,
            hasModel: !!requestBody.model,
            hasVisitorIp: !!requestBody.visitorIp,
            hasBrowserRevealData: !!requestBody.browserRevealData,
            hasSnitcherSessionId: !!snitcherSessionId,
            hasBookingContext: !!requestBody.bookingContext,
            isFormAssistant: requestBody.isFormAssistant,
            isPostConversion: requestBody.isPostConversion,
            queryLength: query.length,
        });

        return requestBody;
    }

    private validateMessage(message: string): string | null {
        if (!message || typeof message !== 'string' || !message.trim()) {
            return 'Empty or invalid question';
        }
        if (message.length > MAX_QUERY_CHARS) {
            return `Message too long (${message.length} chars, max ${MAX_QUERY_CHARS})`;
        }
        return null;
    }

    public getSessionId(): string {
        return this.sessionId;
    }

    public newSession(): void {
        // Drop the pinned language for the old session before rotating the id so
        // the fresh conversation re-detects language from the current page.
        this.storage.removeItem(this.getLanguageStorageKey());

        // Generate a new session ID for a fresh conversation
        this.sessionId = this.generateSessionId();

        // Update sessionStorage with the new session ID
        const storageKey = this.getStorageKey();
        this.storage.setItem(storageKey, this.sessionId);
    }

    /**
     * Adopt an externally-created session id (resume deep-link, IX-3738).
     *
     * The widget continues the exact server-side session (LangGraph memory is
     * keyed by this id), so follow-up questions pick up where the ChatGPT-side
     * conversation left off. Persisted in the same sessionStorage slot a
     * generated id would use, so refreshes keep the adopted session.
     */
    public adoptSessionId(sessionId: string): void {
        // Drop any language pinned to the previous (generated) session id.
        this.storage.removeItem(this.getLanguageStorageKey());
        this.sessionId = sessionId;
        this.storage.setItem(this.getStorageKey(), sessionId);
        logger.debug('InboundX Service: Adopted resumed session:', sessionId);
    }

    /**
     * Fetch the prior turns of a resumable session (IX-3738).
     *
     * Throws on non-2xx (expired session → 404, foreign session → 403) so the
     * caller can fall back to a normal fresh-session boot.
     */
    async fetchResumeHistory(
        sessionId: string
    ): Promise<{ role: 'user' | 'assistant'; content: string }[]> {
        const response = await fetch(`${this.config.apiUrl}/api/lightrag/resume`, {
            method: 'POST',
            headers: this.createHeaders(),
            body: JSON.stringify({ siteName: this.config.siteName, sessionId }),
        });
        if (!response.ok) {
            throw new Error(`Resume failed: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data.messages) ? data.messages : [];
    }

    async query(
        message: string,
        messageSource?: string,
        isFormAssistant?: boolean,
        isPostConversion?: boolean,
        signal?: AbortSignal
    ): Promise<InboundXResponse> {
        try {
            // Defensive check for empty or undefined message
            const validationError = this.validateMessage(message);
            if (validationError) {
                logger.error('InboundX Service: Empty or invalid query message:', message);
                return {
                    content: '',
                    error: validationError,
                    sessionId: this.sessionId,
                };
            }

            // Flush Snitcher data before making the request to ensure session data is synced
            await flushSnitcherData();

            const messageId = this.generateMessageId();

            // Resolve the conversation language once and pin it to the session
            // (see resolveConversationLanguage). Detected from the page URL/site
            // config at the first message, then reused on every later message so
            // the language doesn't flip if the visitor navigates to a page in a
            // different locale mid-conversation (IX-3322). 'auto' stays per-message
            // backend detection. We keep supported regional conversation
            // locales (e.g. en-gb) so the backend can route content variants
            // for the same LLM language, while unsupported regional locales
            // (e.g. fr-fr) collapse to their base language.
            const conversationLanguage = this.resolveConversationLanguage();

            logger.debug('🌐 InboundXService language resolution:', {
                configLanguage: this.config.conversationLanguage,
                finalLanguage: conversationLanguage,
                message: `${message.substring(0, 50)}...`,
            });

            const requestBody = this.createRequestBody(
                message,
                messageId,
                conversationLanguage,
                messageSource,
                undefined,
                isFormAssistant,
                isPostConversion
            );
            const headers = this.createHeaders();

            const response = await fetch(`${this.config.apiUrl}/api/lightrag/query`, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestBody),
                signal,
            });

            if (!response.ok) {
                const errorText = await response.text();
                logger.error('InboundX Service: Error response:', errorText);
                throw new Error(`InboundX API Error: ${response.status} - ${errorText}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            const content = data.response || 'No response received.';

            if (!data.response) {
                logger.error('InboundX Service: No response received.');
                return {
                    content: '',
                    error: 'No response received.',
                    sessionId: this.sessionId,
                };
            }

            return {
                content,
                sessionId: data.sessionId || this.sessionId,
                messageId: messageId, // Return the same messageId that was sent to the API
            };
        } catch (error) {
            logger.error('InboundX Service: Error:', error, {
                sessionId: this.sessionId,
                apiUrl: this.config.apiUrl,
            });
            return {
                content: '',
                error: error instanceof Error ? error.message : 'Unknown error',
                sessionId: this.sessionId,
            };
        }
    }

    async queryStream(
        message: string,
        onChunk: (chunk: StreamingResponse) => void,
        onComplete?: (finalResponse: string) => void,
        onError?: (error: string) => void,
        signal?: AbortSignal,
        messageSource?: string,
        bookingContext?: BookingContext,
        isFormAssistant?: boolean,
        isPostConversion?: boolean
    ): Promise<void> {
        let streamFailurePhase: 'start' | 'read' = 'read';
        let finalStreamStartAttempt: number | undefined;
        let finalStreamStartMaxAttempts: number | undefined;

        try {
            // Defensive check for empty or undefined message
            const validationError = this.validateMessage(message);
            if (validationError) {
                logger.error(
                    'InboundX Service: Empty or invalid streaming query message:',
                    message
                );
                onError?.(validationError);
                return;
            }

            // Flush Snitcher data before making the request to ensure session data is synced
            await flushSnitcherData();

            const messageId = this.generateMessageId();

            // Resolve the conversation language once and pin it to the session
            // (see resolveConversationLanguage). Detected from the page URL/site
            // config at the first message, then reused on every later message so
            // the language doesn't flip if the visitor navigates to a page in a
            // different locale mid-conversation (IX-3322). 'auto' stays per-message
            // backend detection. We keep supported regional conversation
            // locales (e.g. en-gb) so the backend can route content variants
            // for the same LLM language, while unsupported regional locales
            // (e.g. fr-fr) collapse to their base language.
            const conversationLanguage = this.resolveConversationLanguage();

            logger.debug('🌐 InboundXService language resolution:', {
                configLanguage: this.config.conversationLanguage,
                finalLanguage: conversationLanguage,
                message: `${message.substring(0, 50)}...`,
            });

            const requestBody = this.createRequestBody(
                message,
                messageId,
                conversationLanguage,
                messageSource,
                bookingContext,
                isFormAssistant,
                isPostConversion
            );

            // Since EventSource doesn't support POST with body, we'll use fetch with stream.
            // The bearer, when a preview host supplies one, is what lets the API return
            // backoffice-only extras (retrieval provenance) without trusting the request body.
            const previewAuthToken = (await this.config.getPreviewAuthToken?.()) ?? null;
            const headers = {
                ...this.createHeaders(),
                Accept: 'text/plain',
                ...(previewAuthToken ? { Authorization: `Bearer ${previewAuthToken}` } : {}),
            };

            const MAX_STREAM_START_RETRIES = 1;
            const STREAM_RETRY_DELAY_MS = 500;
            const STREAM_START_TIMEOUT_MS = 30000;
            const waitForRetryDelay = (delayMs: number): Promise<boolean> => {
                if (!signal) {
                    return new Promise((resolve) => {
                        setTimeout(() => resolve(true), delayMs);
                    });
                }
                if (signal.aborted) {
                    return Promise.resolve(false);
                }
                return new Promise((resolve) => {
                    const onAbort = () => {
                        clearTimeout(timeoutId);
                        resolve(false);
                    };
                    const timeoutId = setTimeout(() => {
                        signal.removeEventListener('abort', onAbort);
                        resolve(true);
                    }, delayMs);
                    signal.addEventListener('abort', onAbort, { once: true });
                });
            };

            let response: Response | null = null;

            for (let attempt = 0; attempt <= MAX_STREAM_START_RETRIES; attempt++) {
                // Create AbortController for timeout, or use parent signal if provided
                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    logger.warn('⏱️ Streaming request timeout after 30 seconds');
                    controller.abort();
                }, STREAM_START_TIMEOUT_MS);

                let onParentAbort: (() => void) | undefined;

                try {
                    // If parent signal is provided, abort our controller when parent is aborted
                    if (signal) {
                        onParentAbort = () => {
                            logger.debug('🚫 Parent signal aborted, cancelling stream');
                            controller.abort();
                        };
                        signal.addEventListener('abort', onParentAbort);
                    }

                    response = await fetch(`${this.config.apiUrl}/api/lightrag/query/stream`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(requestBody),
                        signal: controller.signal,
                    });
                    clearTimeout(timeoutId);

                    // 429 = another request in-flight for this session. Retry once after
                    // Retry-After: a genuine double-submit usually clears within seconds, and
                    // a leaked backend slot self-heals via its TTL. If still busy, fall through
                    // to the !response.ok handler below (clean localized message, no raw JSON).
                    if (
                        response.status === 429 &&
                        attempt < MAX_STREAM_START_RETRIES &&
                        !signal?.aborted
                    ) {
                        const retryAfterMs = Math.min(
                            (Number.parseInt(response.headers.get('Retry-After') ?? '', 10) || 5) *
                                1000,
                            6000
                        );
                        logger.debug('InboundX Service: 429 rate limited, retrying once', {
                            sessionId: this.sessionId,
                            retryAfterMs,
                        });
                        if (!(await waitForRetryDelay(retryAfterMs))) {
                            return;
                        }
                        continue;
                    }
                    break;
                } catch (fetchError) {
                    clearTimeout(timeoutId);

                    if (fetchError instanceof Error && fetchError.name === 'AbortError') {
                        // Check if it was the parent signal that aborted
                        if (signal?.aborted) {
                            logger.debug('🚫 Stream cancelled by parent');
                            return; // Don't call onError for intentional cancellation
                        }
                        const timeoutError =
                            'Request timeout - the server took too long to respond';
                        logger.error('⏱️ Streaming request aborted due to timeout', fetchError, {
                            sessionId: this.sessionId,
                            apiUrl: this.config.apiUrl,
                            siteName: this.config.siteName,
                            frontendVersion: FRONTEND_VERSION,
                            streamPhase: 'start',
                            errorCategory: 'stream_idle_timeout',
                            attempt: attempt + 1,
                            maxAttempts: MAX_STREAM_START_RETRIES + 1,
                        });
                        onError?.(timeoutError);
                        return;
                    }

                    const classifiedError = classifyStreamingError(fetchError);
                    const shouldRetry =
                        classifiedError.category === 'network_stream_failure' &&
                        attempt < MAX_STREAM_START_RETRIES &&
                        !signal?.aborted;

                    const errorContext = {
                        sessionId: this.sessionId,
                        apiUrl: this.config.apiUrl,
                        siteName: this.config.siteName,
                        frontendVersion: FRONTEND_VERSION,
                        streamPhase: 'start',
                        errorCategory: classifiedError.category,
                        attempt: attempt + 1,
                        maxAttempts: MAX_STREAM_START_RETRIES + 1,
                        willRetry: shouldRetry,
                    };

                    if (shouldRetry) {
                        logger.debug('InboundX Service: Stream start request failed, retrying', {
                            ...errorContext,
                            message: classifiedError.message,
                        });
                    }

                    if (shouldRetry) {
                        if (signal?.aborted) {
                            return;
                        }
                        if (!(await waitForRetryDelay(STREAM_RETRY_DELAY_MS))) {
                            return;
                        }
                        continue;
                    }

                    streamFailurePhase = 'start';
                    finalStreamStartAttempt = attempt + 1;
                    finalStreamStartMaxAttempts = MAX_STREAM_START_RETRIES + 1;
                    throw fetchError;
                } finally {
                    if (signal && onParentAbort) {
                        signal.removeEventListener('abort', onParentAbort);
                    }
                }
            }

            if (!response) {
                onError?.('No response received from streaming endpoint');
                return;
            }

            if (!response.ok) {
                // 429 survived the retry above: another request is genuinely in-flight.
                // Send a stable marker (not the raw stream_error JSON) so the UI shows a
                // friendly, localized "wait a few seconds and retry" message.
                if (response.status === 429) {
                    onError?.('RATE_LIMITED: a request is already in progress for this session');
                    return;
                }
                const errorText = await response.text();
                // Pass error to callback - it will be logged with proper categorization in useStreamingMessage
                onError?.(errorText);
                return;
            }

            if (!response.body) {
                onError?.('No response body available for streaming');
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let finalResponse = '';
            let stopStreaming = false;
            let hasCompleted = false;

            // Idle timeout for stream reads - if no data received within this time, consider it a timeout
            const STREAM_IDLE_TIMEOUT_MS = 30000; // 30 seconds idle timeout

            /**
             * Read from stream with timeout protection.
             * If no data is received within the timeout period, reject with timeout error.
             * This prevents the reader from hanging indefinitely when the backend stops sending data.
             */
            const readWithIdleTimeout = async (): Promise<ReadableStreamReadResult<Uint8Array>> => {
                return new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => {
                        reject(new Error('Stream idle timeout - no data received from server'));
                    }, STREAM_IDLE_TIMEOUT_MS);

                    reader.read().then(
                        (result) => {
                            clearTimeout(timeoutId);
                            resolve(result);
                        },
                        (error) => {
                            clearTimeout(timeoutId);
                            reject(error);
                        }
                    );
                });
            };

            try {
                while (true) {
                    const { done, value } = await readWithIdleTimeout();

                    if (done) {
                        if (onComplete && !hasCompleted) {
                            onComplete(finalResponse);
                            hasCompleted = true;
                        }
                        break;
                    }

                    // Decode the chunk
                    const chunk = decoder.decode(value, { stream: true });
                    buffer += chunk;

                    // Process complete lines (Server-Sent Events format)
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const jsonData = line.slice(6); // Remove 'data: ' prefix

                                // Debug logging to see raw JSON data
                                const parsedData: StreamingResponse = JSON.parse(jsonData);

                                // Update session ID if provided
                                if (
                                    parsedData.sessionId &&
                                    parsedData.sessionId !== this.sessionId
                                ) {
                                    this.sessionId = parsedData.sessionId;
                                }

                                // Accumulate content for final response
                                if (parsedData.type === 'token') {
                                    finalResponse += parsedData.content;
                                } else if (parsedData.type === 'complete') {
                                    finalResponse = parsedData.content;
                                }

                                // Call the chunk handler
                                onChunk(parsedData);

                                // Handle errors
                                if (parsedData.type === 'error') {
                                    logger.error('InboundX Service: Stream error chunk', {
                                        content: parsedData.content,
                                        sessionId: this.sessionId,
                                        apiUrl: this.config.apiUrl,
                                        siteName: this.config.siteName,
                                        frontendVersion: FRONTEND_VERSION,
                                        streamPhase: 'read',
                                        errorCategory: 'unknown_stream_error',
                                    });
                                    onError?.(parsedData.content);
                                    stopStreaming = true;
                                    break;
                                } else if (parsedData.type === 'stream_error') {
                                    // Stream error is a system error that should be logged and displayed differently
                                    logger.error(
                                        'InboundX Service: Stream error received:',
                                        parsedData,
                                        {
                                            sessionId: this.sessionId,
                                            apiUrl: this.config.apiUrl,
                                            siteName: this.config.siteName,
                                            frontendVersion: FRONTEND_VERSION,
                                            streamPhase: 'read',
                                            errorCategory: 'unknown_stream_error',
                                        }
                                    );

                                    // Log detailed debug information if available
                                    if (parsedData.debug_info) {
                                        logger.error('🔍 DETAILED ERROR INFO:', {
                                            error_type: parsedData.debug_info.error_type,
                                            error_message: parsedData.debug_info.error_message,
                                            site_name: parsedData.debug_info.site_name,
                                            session_id: parsedData.debug_info.session_id,
                                            timestamp: parsedData.debug_info.timestamp,
                                        });
                                        logger.error(
                                            '📋 STACK TRACE:',
                                            parsedData.debug_info.stack_trace
                                        );
                                    }

                                    onError?.(
                                        `System error: ${parsedData.message || parsedData.error || 'Unknown stream error'}`
                                    );
                                    stopStreaming = true;
                                    break;
                                } else if (parsedData.type === 'complete') {
                                    if (onComplete && !hasCompleted) {
                                        onComplete(finalResponse);
                                        hasCompleted = true;
                                    }
                                    stopStreaming = true;
                                    break;
                                }
                            } catch (parseError) {
                                logger.error(
                                    'InboundX Service: Error parsing streaming data:',
                                    parseError,
                                    {
                                        sessionId: this.sessionId,
                                        apiUrl: this.config.apiUrl,
                                        siteName: this.config.siteName,
                                        frontendVersion: FRONTEND_VERSION,
                                        streamPhase: 'read',
                                        errorCategory: 'unknown_stream_error',
                                    }
                                );
                                // Continue processing other chunks
                            }
                        }
                    }

                    // If an error has occurred, cancel the reader and stop the outer loop
                    if (stopStreaming) {
                        try {
                            reader.cancel();
                        } catch {
                            // ignore
                        }
                        break;
                    }
                }
            } finally {
                reader.releaseLock();
            }
        } catch (error) {
            const classifiedError = classifyStreamingError(error);
            const isIdleTimeout = classifiedError.category === 'stream_idle_timeout';

            if (isIdleTimeout) {
                logger.warn(
                    '⏱️ InboundX Service: Stream idle timeout - backend stopped responding',
                    {
                        sessionId: this.sessionId,
                        apiUrl: this.config.apiUrl,
                        siteName: this.config.siteName,
                        frontendVersion: FRONTEND_VERSION,
                        streamPhase: streamFailurePhase,
                        errorCategory: classifiedError.category,
                        attempt: finalStreamStartAttempt,
                        maxAttempts: finalStreamStartMaxAttempts,
                    }
                );
                // Use a standardized timeout message that will be recognized by error categorization
                onError?.('Request timeout - the server stopped responding');
            } else {
                logger.error('InboundX Service: Streaming error:', error, {
                    sessionId: this.sessionId,
                    apiUrl: this.config.apiUrl,
                    siteName: this.config.siteName,
                    frontendVersion: FRONTEND_VERSION,
                    streamPhase: streamFailurePhase,
                    errorCategory: classifiedError.category,
                    attempt: finalStreamStartAttempt,
                    maxAttempts: finalStreamStartMaxAttempts,
                });
                onError?.(classifiedError.message);
            }
        }
    }

    async resetMemory(): Promise<boolean> {
        try {
            logger.debug(
                'InboundX Service: Resetting memory for site:',
                this.config.siteName,
                'Session:',
                this.sessionId
            );

            const headers = this.createHeaders();
            logger.debug('🚀 InboundX API Call - Reset Memory:', {
                url: `${this.config.apiUrl}/api/lightrag/reset_memory`,
                method: 'POST',
                siteName: this.config.siteName,
                sessionId: this.sessionId,
            });

            const response = await fetch(`${this.config.apiUrl}/api/lightrag/reset_memory`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    siteName: this.config.siteName,
                    sessionId: this.sessionId,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error during reset: ${response.status}`);
            }

            const data = await response.json();
            logger.debug('InboundX Service: Memory reset response:', data.status);

            // Generate a new session ID after resetting memory
            this.newSession();

            return true;
        } catch (error) {
            logger.error('InboundX Service: Error resetting memory:', error, {
                sessionId: this.sessionId,
                apiUrl: this.config.apiUrl,
            });
            return false;
        }
    }

    updateConfig(newConfig: Partial<InboundXConfig>) {
        this.config = { ...this.config, ...newConfig };
    }

    getCurrentSite(): string {
        return this.config.siteName;
    }

    getApiUrl(): string {
        return this.config.apiUrl;
    }

    async getHealth(): Promise<{
        version?: string;
        environment?: string;
        status: string;
    }> {
        try {
            return await fetchApiHealth(this.config.apiUrl);
        } catch (error) {
            // Use debug - caller (useServiceInitialization) will log the final error after retries
            logger.debug('InboundX Service: Health check failed:', error, {
                apiUrl: this.config.apiUrl,
            });
            throw error;
        }
    }

    async checkApiVersion(): Promise<{
        isCompatible: boolean;
        frontendVersion: string;
        backendVersion?: string;
        environment?: string;
        error?: string;
    }> {
        try {
            const versionUrl = `${this.config.apiUrl}/api/version`;
            let response: Response;

            // Use network adapter if available (for Chrome extension content scripts)
            if (this.config.networkAdapter) {
                try {
                    // Note: NetworkAdapter currently only supports POST, so we use a workaround
                    // by making a GET request through fetch if possible, or handle via background script
                    const headers = this.createHeaders();

                    response = await fetch(versionUrl, {
                        method: 'GET',
                        headers,
                    });
                } catch {
                    // If fetch fails (e.g., in Chrome extension content script), we need to handle via background
                    logger.info(
                        'Direct fetch failed, version check not supported with current network adapter'
                    );
                    return {
                        isCompatible: true, // Assume compatible to allow widget to load
                        frontendVersion: FRONTEND_VERSION,
                        error: 'Version check skipped due to network restrictions',
                    };
                }
            } else {
                // Direct fetch for non-extension environments
                const headers = this.createHeaders();

                response = await fetch(versionUrl, {
                    method: 'GET',
                    headers,
                });
            }

            if (!response.ok) {
                const errorText = await response.text();
                // Log as debug instead of error since this will be caught by retry logic
                // Only truly unrecoverable errors after all retries should go to Sentry
                logger.debug('InboundX Service: Version check failed (will retry):', errorText);
                return {
                    isCompatible: false,
                    frontendVersion: FRONTEND_VERSION,
                    error: `Version check failed: ${response.status} - ${errorText}`,
                };
            }

            const versionData = await response.json();
            const backendVersion = versionData.version;
            const environment = versionData.environment;

            const isCompatible = isVersionCompatible(FRONTEND_VERSION, backendVersion);

            logger.debug(
                `InboundX Service: Version check - Frontend: ${FRONTEND_VERSION}, Backend: ${backendVersion}, Environment: ${environment}, Compatible: ${isCompatible}`
            );

            return {
                isCompatible,
                frontendVersion: FRONTEND_VERSION,
                backendVersion,
                environment,
            };
        } catch (error) {
            // Log as debug instead of error since this will be caught by retry logic
            // Only truly unrecoverable errors after all retries should go to Sentry
            logger.debug('InboundX Service: Version check error (will retry):', error, {
                apiUrl: this.config.apiUrl,
            });
            return {
                isCompatible: false,
                frontendVersion: FRONTEND_VERSION,
                error: error instanceof Error ? error.message : 'Unknown version check error',
            };
        }
    }

    /**
     * Track visitor impression and trigger enrichment if enabled.
     *
     * This is a fire-and-forget call that should be made when the widget loads,
     * after Snitcher Radar is ready. The backend will check if the
     * enrich_all_visitors feature is enabled and trigger enrichment in the background.
     *
     * @returns Promise that resolves when the request completes (errors are silently ignored)
     */
    async trackVisitorImpression(): Promise<void> {
        try {
            // Flush Snitcher data first. The impression only queues the session
            // UUID (the backend resolves it later), but Snitcher cannot ingest a
            // session it has never received — flushing starts that ~40s clock now
            // instead of whenever the SDK next batches, so the sweep finds the
            // session ready on its first look. Same call the chat path makes.
            await flushSnitcherData();

            const personId = getPersonId();
            const snitcherSessionId = getSnitcherSessionId();

            const requestBody: Record<string, unknown> = {
                siteName: this.config.siteName,
                sessionId: this.sessionId,
            };

            if (personId) {
                requestBody.personId = personId;
            }

            if (this.config.visitorIp) {
                requestBody.visitorIp = this.config.visitorIp;
            }

            if (this.config.browserRevealData) {
                requestBody.browserRevealData = this.config.browserRevealData;
            }

            if (snitcherSessionId) {
                requestBody.snitcherSessionId = snitcherSessionId;
            }

            const identificationConsent = getIdentificationConsent();
            if (identificationConsent !== null) {
                requestBody.identificationConsent = identificationConsent;
            }

            logger.debug('📤 [Visitor Impression] Sending impression data', {
                siteName: requestBody.siteName,
                hasPersonId: !!personId,
                hasVisitorIp: !!requestBody.visitorIp,
                hasBrowserRevealData: !!requestBody.browserRevealData,
                hasSnitcherSessionId: !!snitcherSessionId,
            });

            const response = await fetch(`${this.config.apiUrl}/api/visitor/impression`, {
                method: 'POST',
                headers: this.createHeaders(),
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                // Silently fail - this is fire-and-forget
                logger.debug('[Visitor Impression] Request failed silently', {
                    status: response.status,
                });
                return;
            }

            const data = await response.json();
            logger.debug('[Visitor Impression] Response:', data);
        } catch (error) {
            // Silently fail - don't break widget if enrichment fails
            logger.debug('[Visitor Impression] Request error (ignored):', error);
        }
    }

    async capturePostRequest(
        payload: PostRequestCaptureEvent,
        options: { keepalive?: boolean; fetchImpl?: typeof fetch } = {}
    ): Promise<boolean> {
        try {
            const fetchFn = options.fetchImpl ?? fetch;
            const response = await fetchFn(`${this.config.apiUrl}/api/post-capture`, {
                method: 'POST',
                headers: this.createHeaders(),
                body: JSON.stringify(payload),
                keepalive: options.keepalive,
            });

            if (!response.ok) {
                logger.debug('[POST Capture] Request failed', {
                    status: response.status,
                    source: payload.source,
                    requestUrl: payload.requestUrl,
                });
                return false;
            }

            return true;
        } catch (error) {
            logger.debug('[POST Capture] Request error (ignored):', {
                error: error instanceof Error ? error.message : String(error),
                source: payload.source,
                requestUrl: payload.requestUrl,
            });
            return false;
        }
    }
}

// Factory function pour créer une instance
export const createInboundXService = (config: InboundXConfig): InboundXService => {
    return new InboundXService(config);
};

/**
 * The chat service the widget is currently running on.
 *
 * Surfaces outside the React tree (WebMCP tool registration) must reuse this
 * instance rather than constructing their own: the resolved API endpoint can
 * differ from the loader's (`system.api_endpoint` override) and the session id
 * is cached per instance, so a second service would talk to the wrong host and
 * keep a dead session after `resetMemory()` rotates this one.
 */
let activeService: InboundXService | null = null;

export const setActiveInboundXService = (service: InboundXService | null): void => {
    activeService = service;
};

export const getActiveInboundXService = (): InboundXService | null => activeService;
